import { ApplePayButton, getISO3166CodeFromCurrency, logger } from '@trx/shared';
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { decryptApplePayData } from '../../../api';
import { validateMerchant } from '../../../api/initApi';
import { DecryptStatus } from '../../../constants';
import { useDataContext } from '../../../context/dataContainer';
import { getAppleWalletInfo, getAppleWalletInfoContactFields, getButtonColorUtil } from '../../../utils';
import {
  applePayBtnLimits,
  clamp,
  DEFAULT_APPLE_PAY_BUTTON_TYPE,
  isValidApplePayButtonType,
  sanitizeBusinessName,
  toAppleButtonType,
} from './util';
import type { FieldComponentProps, IPayError, IPaySuccess } from '../../../types';
import type { ApplePayAuthorizationResult, ApplePayButtonStyle, ApplePayPayment } from '@trx/shared';

export const ApplePay: React.FC<FieldComponentProps> = (props: FieldComponentProps) => {
  const [search] = useSearchParams();
  const buttonType = search.get('buttonType');
  const buttonColor = search.get('buttonColor');
  const buttonLocale = search.get('buttonLocale');
  const buttonRadius = search.get('buttonRadius');
  const width = search.get('width');
  const height = search.get('height');
  const includeBIN = search.get('includeBIN') === 'true';
  const businessName = sanitizeBusinessName(search.get('businessName'));
  const walletInfoFlags = {
    isBilling: search.get('enableBillingWalletInfo') === 'true',
    isShipping: search.get('enableShippingWalletInfo') === 'true',
  };
  const { requiredBillingContactFields, requiredShippingContactFields } = getAppleWalletInfoContactFields(walletInfoFlags);

  const { currentLanguage = 'en', toggleFetching, isFetching, sendApplePayTokenToLib, notifyApplePayBtnClicked, updateHeight } = props;
  const { amount, paymentMethod, currencyCode, style, encryption, softDescriptors, description } = useDataContext();

  const totalLabel = businessName ?? softDescriptors?.merchantName ?? description;

  const validBtnType = isValidApplePayButtonType(buttonType)
    ? buttonType
    : isValidApplePayButtonType(style?.applePayButtonType)
      ? style.applePayButtonType
      : DEFAULT_APPLE_PAY_BUTTON_TYPE;

  // The shared button appears asynchronously (after its own availability check),
  // so the frame height is updated whenever the button size changes.
  // TODO - remove the ResizeObserver (and the wrapper div) after `onReady` is added to shared
  const wrapperRef = useRef<HTMLDivElement>(null);
  const updateHeightRef = useRef(updateHeight);

  useEffect(() => {
    // updateHeight is recreated on every parent render, so keep the latest one in the ref for the observer attached once below
    updateHeightRef.current = updateHeight;
  });

  useEffect(() => {
    if (!wrapperRef.current) return undefined;

    const resizeObserver = new ResizeObserver(() => updateHeightRef.current?.());
    resizeObserver.observe(wrapperRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const { iso3166Code, iso4217CurrencyCode } = getISO3166CodeFromCurrency(currencyCode);

  const handleClick = () => {
    notifyApplePayBtnClicked?.();
    toggleFetching(true);
  };

  const handleValidateMerchant = (validationURL: string, domain: string) => validateMerchant({ validationUrl: validationURL, domain });

  const handlePaymentAuthorized = async (payment: ApplePayPayment): Promise<ApplePayAuthorizationResult> => {
    try {
      if (!encryption?.key || !encryption.keyId) {
        sendApplePayTokenToLib({ status: DecryptStatus.FAILED, message: 'Public key is missing' });
        return { status: 'failure' };
      }

      const response = await decryptApplePayData({
        applePaymentData: payment.token,
        publicKey: encryption.key,
        keyId: encryption.keyId,
        includeBIN,
      });

      if ((response as IPayError).title) {
        const { title } = response as IPayError;
        sendApplePayTokenToLib({ status: DecryptStatus.FAILED, message: title });
        toggleFetching(false);
        return { status: 'failure' };
      }

      const walletInfo = getAppleWalletInfo(payment, walletInfoFlags);

      sendApplePayTokenToLib({ status: DecryptStatus.SUCCESS, data: { ...response as IPaySuccess, ...(walletInfo && { walletInfo }) } });
      return { status: 'success' };
    } catch (e) {
      sendApplePayTokenToLib({ status: DecryptStatus.FAILED, message: (e as Error).message });
      toggleFetching(false);
      return { status: 'failure' };
    }
  };

  const handleCancel = () => {
    sendApplePayTokenToLib({ status: DecryptStatus.CANCELLED });
    toggleFetching(false);
  };

  const handleError = (e: Error) => {
    toggleFetching(false);
    sendApplePayTokenToLib({ status: DecryptStatus.FAILED, message: e.message });
    logger.log(`ApplePay error: ${String(e)}`);
  };

  const buttonWidth = clamp(width, applePayBtnLimits.width(validBtnType));
  const buttonHeight = clamp(height, applePayBtnLimits.height(validBtnType));

  return (
    <div ref={wrapperRef}>
      <ApplePayButton
        amount={amount}
        label={totalLabel}
        countryCode={iso3166Code}
        currencyCode={iso4217CurrencyCode}
        creditNetworks={paymentMethod.credit}
        requiredBillingContactFields={requiredBillingContactFields}
        requiredShippingContactFields={requiredShippingContactFields}
        buttonType={toAppleButtonType(validBtnType)}
        buttonStyle={(buttonColor ?? getButtonColorUtil(style?.bodyBg)) as ApplePayButtonStyle}
        buttonLocale={buttonLocale ?? (currentLanguage || 'en-US')}
        buttonBorderRadius={clamp(buttonRadius, applePayBtnLimits.borderRadius())}
        buttonHeight={buttonHeight ? `${String(buttonHeight)}px` : undefined}
        buttonWidth={buttonWidth ? `${String(buttonWidth)}px` : undefined}
        isDisabled={isFetching}
        // The field frame is only as tall as the button, so show the Apple Pay code in its own window
        renderApplePayCodeAs='window'
        className='apple-pay-button-wrapper'
        onClick={handleClick}
        onValidateMerchant={handleValidateMerchant}
        onPaymentAuthorized={handlePaymentAuthorized}
        onCancel={handleCancel}
        onError={handleError}
        // TODO - uncomment after adding to shared
        // onReady={updateHeight}
      />
    </div>
  );
};
